// Situação de uma mensagem enviada pela caixa (painel do agente e terminal ao vivo): na fila,
// entregue, ou não entregue com o motivo. A tela consulta GET /api/messages/:id a cada 2 s até
// chegar a um estado final (ou até o limite, se a sessão nunca pegar a mensagem).
//
// A última situação fica guardada aqui (por agente): o compositor do painel é refeito quando a
// presença muda, e a linha da situação volta sozinha com o que já se sabia.
//
// Puro na maior parte (textoDaSituacao, htmlDaSituacao); o acompanhamento usa fetch e timer.

import type { MessageStatus } from '../core/types.js';

/** As chaves de i18n que a situação usa (o `t` do i18n.ts serve, porque o conjunto é um subconjunto dele). */
export type ChaveSituacao = 'msg.na_fila' | 'msg.entregue' | 'msg.nao_entregue';
export type Traduz = (chave: ChaveSituacao, vars?: Record<string, string | number>) => string;

/** A situação de uma mensagem, com o agente a que ela pertence. */
export interface Situacao extends MessageStatus {
  agentId: string;
}

/** A última mensagem enviada pela tela (uma por vez; o desktop e o ao vivo leem a mesma). */
let ultima: Situacao | undefined;

/** O texto da situação: 'na fila', 'entregue' ou 'não entregue: <motivo>'. Sem situação, vazio. */
export function textoDaSituacao(situacao: Pick<MessageStatus, 'state' | 'error'> | undefined, traduz: Traduz): string {
  if (!situacao) return '';
  if (situacao.state === 'queued') return traduz('msg.na_fila');
  if (situacao.state === 'delivered') return traduz('msg.entregue');
  return traduz('msg.nao_entregue', { motivo: situacao.error ?? '' });
}

/** Um estado final não muda mais: para de perguntar. */
export function ehFinal(state: MessageStatus['state']): boolean {
  return state !== 'queued';
}

/** A mensagem acabou de entrar na caixa: já aparece como "na fila" até a primeira resposta do servidor. */
export function marcaEnvio(agentId: string, id: string): void {
  ultima = { agentId, id, state: 'queued' };
}

/** A situação da última mensagem do agente (undefined se a última é de outro agente). */
export function situacaoDoAgente(agentId: string): Situacao | undefined {
  return ultima?.agentId === agentId ? ultima : undefined;
}

function escapa(texto: string): string {
  return texto.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}

/** A linha da situação para o HTML do compositor. Sem situação, sai escondida (o acompanhamento a preenche). */
export function htmlDaSituacao(agentId: string, traduz: Traduz, id: string, classe: string): string {
  const situacao = situacaoDoAgente(agentId);
  const texto = textoDaSituacao(situacao, traduz);
  return `<p id="${id}" class="${classe}" aria-live="polite" data-estado="${situacao?.state ?? ''}"${texto ? '' : ' hidden'}>${escapa(texto)}</p>`;
}

/** Escreve a situação no elemento (o `alvo` pode ter sumido com a repintura: então não faz nada). */
export function pintaSituacao(el: HTMLElement | null, agentId: string, traduz: Traduz): void {
  if (!el) return;
  const situacao = situacaoDoAgente(agentId);
  const texto = textoDaSituacao(situacao, traduz);
  el.textContent = texto;
  el.dataset.estado = situacao?.state ?? '';
  el.hidden = !texto;
}

/**
 * Acompanha a mensagem `id` do agente: cada mudança vira a "última" e chama `aoMudar` (que repinta
 * a linha). Para no estado final, no 404 (a mensagem saiu da memória do servidor) ou no limite.
 * Devolve a função que para antes disso.
 */
export function acompanhaMensagem(agentId: string, id: string, aoMudar: () => void, opcoes: { intervaloMs?: number; limiteMs?: number } = {}): () => void {
  const intervalo = opcoes.intervaloMs ?? 2_000;
  const limite = Date.now() + (opcoes.limiteMs ?? 120_000);
  let parado = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const passo = async (): Promise<void> => {
    if (parado) return;
    const res = await fetch(`/api/messages/${encodeURIComponent(id)}`).catch(() => null);
    const json = (await res?.json().catch(() => undefined)) as (Partial<MessageStatus> & { ok?: boolean }) | undefined;
    if (parado) return;
    if (json?.ok === false) {
      parado = true;
      return;
    }
    if (json?.ok && json.state) {
      ultima = { agentId, id, state: json.state, ...(json.error ? { error: json.error } : {}) };
      aoMudar();
      if (ehFinal(json.state)) {
        parado = true;
        return;
      }
    }
    if (Date.now() < limite) timer = setTimeout(passo, intervalo);
    else parado = true;
  };
  timer = setTimeout(passo, intervalo);
  return () => {
    parado = true;
    clearTimeout(timer);
  };
}
